import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { db } from "../../../service/firebase";
import "./TurmaRelatorio.css";

const TurmaRelatorio = () => {
  const { id: turmaId, jogoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [turma, setTurma] = useState(null);
  const [jogo, setJogo] = useState(null);
  const formatarValor = (valor, casas = 2) =>
    Number(valor || 0).toFixed(casas);
  const formatarDeltaOverall = (valor) => {
    const numero = Number(valor || 0);
    if (!Number.isFinite(numero) || numero === 0) {
      return "0.00";
    }
    return `${numero > 0 ? "+" : ""}${numero.toFixed(2)}`;
  };

  useEffect(() => {
    const carregarDados = async () => {
      setError("");
      setLoading(true);
      try {
        const [turmaDoc, jogoDoc] = await Promise.all([
          getDoc(doc(db, "turmas", turmaId)),
          getDoc(doc(db, "jogo_turma", jogoId)),
        ]);

        if (!turmaDoc.exists()) {
          setError("Turma não encontrada.");
          setLoading(false);
          return;
        }

        if (!jogoDoc.exists()) {
          setError("Jogo não encontrado.");
          setLoading(false);
          return;
        }

        const jogoData = { id: jogoDoc.id, ...jogoDoc.data() };
        if (jogoData.id_turma !== turmaId) {
          setError("Este jogo não pertence à turma informada.");
          setLoading(false);
          return;
        }

        setTurma({ id: turmaDoc.id, ...turmaDoc.data() });
        setJogo(jogoData);
      } catch (err) {
        console.error("Erro ao carregar relatório da turma", err);
        setError("Não foi possível carregar o relatório deste jogo.");
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [turmaId, jogoId]);

  const jogadoresSnapshot = useMemo(() => {
    if (!jogo?.relatorio_snapshot?.jogadores) return [];
    return [...jogo.relatorio_snapshot.jogadores].sort(
      (a, b) => (b.pontos || 0) - (a.pontos || 0)
    );
  }, [jogo]);

  const nomePorUsuario = useMemo(() => {
    const mapa = new Map();
    jogadoresSnapshot.forEach((jogador) => {
      if (jogador.id_usuario) {
        mapa.set(jogador.id_usuario, jogador.nome);
      }
    });
    return mapa;
  }, [jogadoresSnapshot]);

  const getNomeDoJogador = useCallback(
    (userId, fallback = "Jogador") => {
      if (!userId) return fallback;
      return nomePorUsuario.get(userId) || fallback;
    },
    [nomePorUsuario]
  );

  const selecaoRodadaDetalhes = useMemo(() => {
    const selecao = jogo?.selecao_rodada || [];
    if (!selecao.length) return [];
    if (typeof selecao[0] === "object") {
      return selecao.map((item) => {
        const userId = item.userId || item.id_usuario || null;
        return {
          userId,
          nome: item.nome || getNomeDoJogador(userId, item.nome || "Jogador"),
          pontos: item.pontos ?? null,
        };
      });
    }
    return selecao.map((userId) => ({
      userId,
      nome: getNomeDoJogador(userId),
    }));
  }, [jogo, getNomeDoJogador]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="body">
          <div className="center">
            <div className="body-content card turma-relatorio-card">
              <span>Carregando relatório...</span>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !jogo) {
    return (
      <>
        <Header />
        <div className="body">
          <div className="center">
            <div className="body-content card turma-relatorio-card">
              <p className="turma-relatorio-error" role="alert">
                {error || "Relatório indisponível."}
              </p>
              <button
                type="button"
                className="btn-default"
                onClick={() => navigate(-1)}
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card turma-relatorio-card">
            <div className="topo-page">
              <strong>
                # Relatório | {turma?.nome || "Turma"} •{" "}
                {jogo?.nome || "Amistoso"}
              </strong>
            </div>
            <div className="turma-relatorio-content">
              <div className="turma-relatorio-highlights">
                <div className="turma-relatorio-highlight">
                  <small>Artilheiro</small>
                  <strong>{getNomeDoJogador(jogo.id_usuario_artilheiro)}</strong>
                </div>
                <div className="turma-relatorio-highlight">
                  <small>Líder de assistências</small>
                  <strong>
                    {getNomeDoJogador(jogo.id_usuario_lider_assistencias)}
                  </strong>
                </div>
                <div className="turma-relatorio-highlight">
                  <small>MVP</small>
                  <strong>{getNomeDoJogador(jogo.id_usuario_mvp)}</strong>
                </div>
              </div>

              {selecaoRodadaDetalhes.length > 0 && (
                <div className="turma-relatorio-section">
                  <h3>Seleção da Rodada</h3>
                  <ul className="turma-relatorio-lista">
                    {selecaoRodadaDetalhes.map((item, index) => (
                      <li key={item.userId || index}>
                        <span>{index + 1}.</span>
                        <strong>{item.nome}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="turma-relatorio-section">
                <h3>Resumo dos Jogadores</h3>
                {jogadoresSnapshot.length === 0 ? (
                  <p>Nenhum dado foi registrado para este jogo.</p>
                ) : (
                  <div className="turma-relatorio-tabela">
                  <div className="turma-relatorio-head">
                    <span>Jogador</span>
                    <span>Gols</span>
                    <span>Assist.</span>
                    <span>Vit.</span>
                    <span>Emp.</span>
                    <span>Der.</span>
                    <span>CS</span>
                    <span>Influência</span>
                    <span>Pontos</span>
                    <span>Des.</span>
                    <span>Bônus</span>
                    <span>OVR Base</span>
                    <span>OVR Pós</span>
                    <span>Δ OVR</span>
                  </div>
                  {jogadoresSnapshot.map((jogador) => (
                      <div
                        key={`${jogador.id_usuario || jogador.nome}`}
                        className="turma-relatorio-row"
                      >
                        <strong>{jogador.nome}</strong>
                        <span>{jogador.gols || 0}</span>
                        <span>{jogador.assistencias || 0}</span>
                        <span>{jogador.vitorias || 0}</span>
                        <span>{jogador.empates || 0}</span>
                        <span>{jogador.derrotas || 0}</span>
                        <span>{jogador.jogosSemTomarGol || 0}</span>
                        <span>
                          {Number(jogador.influenciaVitoria || 0).toFixed(2)}
                        </span>
                        <span>{jogador.pontos || 0}</span>
                        <span>{formatarValor(jogador.desempenhoMedio, 2)}</span>
                        <span>{formatarValor(jogador.bonusDesempenho, 3)}</span>
                        <span>
                          {jogador.overallTurmaBase !== null &&
                          jogador.overallTurmaBase !== undefined
                            ? Number(jogador.overallTurmaBase).toFixed(1)
                            : "-"}
                        </span>
                        <span>
                          {jogador.overallTurmaFinal !== null &&
                          jogador.overallTurmaFinal !== undefined
                            ? Number(jogador.overallTurmaFinal).toFixed(1)
                            : "-"}
                        </span>
                        <span>{formatarDeltaOverall(jogador.overallTurmaDelta)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="turma-relatorio-footer">
              <Link className="btn-default" to={`/turmas/${turmaId}`}>
                Voltar para a turma
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TurmaRelatorio;
